import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { DonationFund, DonationMethod } from '@tabita/contracts';
import { DONATION_FUNDS, DONATION_METHODS } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { givingApi } from '@/services/giving.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

export function RecordDonationDialog({
  onClose,
  onRecorded,
}: {
  onClose: () => void;
  onRecorded: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [donorId, setDonorId] = useState<string | null>(null);
  const [donorLabel, setDonorLabel] = useState('');
  const [fund, setFund] = useState<DonationFund>('GENERAL');
  const [method, setMethod] = useState<DonationMethod>('CASH');
  const [amount, setAmount] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const amountCents = Math.round(Number(amount) * 100);

  const mutation = useMutation({
    mutationFn: () =>
      givingApi.record({
        donorPersonId: donorId,
        fund,
        amount: amountCents,
        currency: 'USD',
        method,
        checkNumber: method === 'CHECK' ? checkNumber.trim() || null : null,
        isTaxDeductible: true,
        note: note.trim() || null,
      }),
    onSuccess: onRecorded,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const canSubmit = Number.isInteger(amountCents) && amountCents > 0;

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="record-donation-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="record-donation-title">{t('giving.donation.record')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <DonorPicker
          label={t('giving.donation.donor')}
          value={donorLabel}
          selectedId={donorId}
          onChange={setDonorLabel}
          onPick={(id, label) => {
            setDonorId(id);
            setDonorLabel(label);
          }}
        />

        <Field
          label={t('giving.donation.amount')}
          name="amount"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0.01"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          dir="ltr"
          required
        />

        <div className="card-fields-row">
          <div className="field">
            <label className="field-label" htmlFor="fund">
              {t('giving.donation.fund')}
            </label>
            <select
              id="fund"
              className="field-input"
              value={fund}
              onChange={(event) => setFund(event.target.value as DonationFund)}
            >
              {DONATION_FUNDS.map((value) => (
                <option key={value} value={value}>
                  {t(`giving.fund.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="method">
              {t('giving.donation.method')}
            </label>
            <select
              id="method"
              className="field-input"
              value={method}
              onChange={(event) => setMethod(event.target.value as DonationMethod)}
            >
              {DONATION_METHODS.map((value) => (
                <option key={value} value={value}>
                  {t(`giving.method.${value}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {method === 'CHECK' && (
          <Field
            label={t('giving.donation.checkNumber')}
            name="checkNumber"
            value={checkNumber}
            onChange={(event) => setCheckNumber(event.target.value)}
            dir="ltr"
          />
        )}

        <Field
          label={t('giving.donation.note')}
          name="note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            {t('giving.donation.record')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function DonorPicker({
  label,
  value,
  selectedId,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  selectedId: string | null;
  onChange: (value: string) => void;
  onPick: (id: string, label: string) => void;
}) {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['people', 'picker', value],
    queryFn: ({ signal }) => peopleApi.list({ search: value, limit: 8 }, signal),
    enabled: value.trim().length >= 2 && selectedId === null,
  });
  const candidates = (query.data?.data ?? []).filter((person) => !person.archivedAt);

  return (
    <div className="member-picker">
      <Field
        label={label}
        name="donor-search"
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('events.registration.choosePerson')}
        hint={t('giving.donation.donorHint')}
      />
      {candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button variant="ghost" onClick={() => onPick(person.id, person.displayName)}>
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
